// src/api/projects.ts
import api from "./api";

export interface ProjectTaskCounts {
  total?: number;
  open?: number;
  closed?: number;
  by_status?: Record<string, number>;
}

export interface ProjectMember {
  id: string;
  name: string;
  email: string | null;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  code: string | null;
  // Links come back in both shapes: the singular field is the primary link
  // (first of the selection) kept for readers that predate multi-select; the
  // arrays carry the whole selection. `*_names` is index-aligned with `*_ids`
  // and holds null for an id whose directory row has since been deleted.
  client_id: string | null;
  client_name: string | null;
  client_ids: string[];
  client_names: (string | null)[];
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_ids: string[];
  vendor_names: (string | null)[];
  // Team — IDs always present; resolved {id, name, email} shapes in `members`.
  member_ids: string[];
  members: ProjectMember[];
  status: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
  start_date: string | null;
  end_date: string | null;
  created_by: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  headOfficeID: string | null;
  branchID: string | null;
  task_counts?: ProjectTaskCounts;
}

export interface ProjectCreatePayload {
  name: string;
  description?: string;
  code?: string;
  // Send `client_ids` / `vendor_ids` for the whole selection; the singular
  // fields still work on their own and are folded in server-side.
  client_id?: string | null;
  vendor_id?: string | null;
  client_ids?: string[];
  vendor_ids?: string[];
  // Pass an array of employee user IDs to assign as team members.
  member_ids?: string[];
  status?: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
  start_date?: string;
  end_date?: string;
}

export interface ProjectUpdatePayload {
  name?: string;
  description?: string;
  code?: string;
  // Pass an array to replace the whole selection; [] clears it; omit both
  // halves of a pair to leave that link untouched.
  client_id?: string | null;
  vendor_id?: string | null;
  client_ids?: string[];
  vendor_ids?: string[];
  // Pass an array to replace the team; [] clears it; omit to leave it.
  member_ids?: string[];
  status?: 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
  start_date?: string;
  end_date?: string;
}

export const projectsApi = {
  list: (params?: {
    search?: string;
    status?: string;
    client_id?: string;
    vendor_id?: string;
    created_by?: string;
    include_archived?: boolean;
    page?: number;
    page_size?: number;
  }) => api.get('/projects', { params }),

  get: (projectId: string) => api.get(`/projects/${projectId}`),

  create: (payload: ProjectCreatePayload) => api.post('/projects', payload),

  update: (projectId: string, payload: ProjectUpdatePayload) =>
    api.patch(`/projects/${projectId}`, payload),

  delete: (projectId: string) => api.delete(`/projects/${projectId}`),

  // Soft delete — moves the project into the "Recently Deleted" bin.
  trash: (projectId: string) => api.post(`/projects/${projectId}/trash`),

  // List soft-deleted projects (with optional date filters).
  listDeleted: (params?: {
    date_from?: string;   // YYYY-MM-DD
    date_to?: string;     // YYYY-MM-DD
    search?: string;
    page?: number;
    page_size?: number;
  }) => api.get('/projects/deleted', { params }),

  // Permanently remove a soft-deleted project (DB row gone).
  deletePermanent: (projectId: string) =>
    api.delete(`/projects/${projectId}/permanent`),

  // Restore a soft-deleted project back to active.
  restore: (projectId: string) =>
    api.post(`/projects/${projectId}/restore`),

  reopen: (projectId: string) => api.post(`/projects/${projectId}/reopen`),

  complete: (projectId: string) => api.post(`/projects/${projectId}/complete`),

  listTasks: (projectId: string, includeClosed = true) =>
    api.get(`/projects/${projectId}/tasks?include_closed=${includeClosed}`),
};

export default projectsApi;
